import React, { useCallback, useEffect, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type { DateSelectArg, DayCellContentArg } from "@fullcalendar/core";
import type { DateClickArg } from "@fullcalendar/interaction";

import * as Styled from "./styles";
import { toPublicSrc } from "../../pages/HouseInfo";
import { SubTitle } from "../../styledTexts";
import {
  DateRange,
  House,
  HouseAvailability,
  addDays,
  isAvailable,
  loadAvailability,
  nightsBetween,
  rangesOverlap,
  startOfDay,
  stayFromSelection,
} from "./availability";

type Props = {
  /** Told the stay whenever the guest changes their selection. */
  onStayChange?: (stay: DateRange | null) => void;
};

function formatDay(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatDayWithYear(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStay(stay: DateRange): string {
  const nights = nightsBetween(stay.start, stay.end);
  const sameYear = stay.start.getFullYear() === stay.end.getFullYear();

  return `${formatDay(stay.start)} – ${
    sameYear ? formatDay(stay.end) : formatDayWithYear(stay.end)
  }, ${stay.end.getFullYear()} · ${nights} ${nights === 1 ? "night" : "nights"}`;
}

function firstConflict(
  availability: HouseAvailability,
  stay: DateRange,
): DateRange | undefined {
  return availability.blocked
    .filter((booked) => rangesOverlap(stay, booked))
    .sort((a, b) => a.start.getTime() - b.start.getTime())[0];
}

function HouseCard({
  house,
  unavailable,
  conflict,
}: {
  house: House;
  unavailable?: boolean;
  conflict?: DateRange;
}) {
  return (
    <Styled.ResultCard $unavailable={unavailable}>
      <Styled.Thumbnail
        src={toPublicSrc(house.flagPhoto)}
        alt={house.tagline}
        loading="lazy"
      />
      <Styled.CardBody>
        <Styled.CardTitle>{house.tagline}</Styled.CardTitle>
        <Styled.CardMeta>
          {house.num_bedrooms} bedrooms · {house.num_beds} beds ·{" "}
          {house.num_baths} baths · sleeps {house.allowed_guests}
        </Styled.CardMeta>
        {unavailable && conflict && (
          <Styled.CardMeta>
            Booked {formatDay(conflict.start)} – {formatDay(conflict.end)}
          </Styled.CardMeta>
        )}
        <Styled.CardLink to={house.route}>
          {unavailable ? "View property" : "View property & request these dates"}
        </Styled.CardLink>
      </Styled.CardBody>
    </Styled.ResultCard>
  );
}

function AvailabilitySearch({ onStayChange }: Props) {
  const [availability, setAvailability] = useState<HouseAvailability[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  useEffect(() => {
    let cancelled = false;

    loadAvailability()
      .then((loaded) => {
        if (cancelled) return;
        setAvailability(loaded);
      })
      .catch((error) => {
        console.warn("Could not load availability", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const stay = useMemo(
    () => (checkIn ? stayFromSelection(checkIn, checkOut) : null),
    [checkIn, checkOut],
  );

  useEffect(() => {
    onStayChange?.(stay);
  }, [stay, onStayChange]);

  // counts one night at a time, memoised as the guest pages through the months
  const countAvailable = useMemo(() => {
    const nightCounts = new Map<number, number>();

    return (date: Date): number => {
      const night = startOfDay(date);
      const key = night.getTime();
      const cached = nightCounts.get(key);
      if (cached !== undefined) return cached;

      const oneNight = { start: night, end: addDays(night, 1) };
      const count = availability.filter((entry) =>
        isAvailable(entry, oneNight),
      ).length;

      nightCounts.set(key, count);
      return count;
    };
  }, [availability]);

  const selectStay = useCallback((start: Date, end: Date | null) => {
    setCheckIn(startOfDay(start));
    setCheckOut(end ? startOfDay(end) : null);
  }, []);

  const handleDateClick = useCallback(
    (arg: DateClickArg) => {
      const clicked = startOfDay(arg.date);
      if (clicked < today) return;

      // first tap picks the day, a later tap extends it into a range
      if (!checkIn || checkOut || clicked <= checkIn) {
        selectStay(clicked, null);
        return;
      }
      selectStay(checkIn, clicked);
    },
    [checkIn, checkOut, selectStay, today],
  );

  const handleSelect = useCallback(
    (arg: DateSelectArg) => {
      // a click already goes through handleDateClick; only take drags here
      if (nightsBetween(arg.start, arg.end) < 2) return;
      selectStay(arg.start, arg.end);
    },
    [selectStay],
  );

  const clearSelection = useCallback(() => {
    setCheckIn(null);
    setCheckOut(null);
  }, []);

  const dayCellClassNames = useCallback(
    (arg: DayCellContentArg): string[] => {
      if (!checkIn) return [];
      const day = startOfDay(arg.date).getTime();

      if (day === checkIn.getTime()) return ["cov-day-edge"];
      if (!checkOut) return [];
      if (day === checkOut.getTime()) return ["cov-day-edge"];

      return day > checkIn.getTime() && day < checkOut.getTime()
        ? ["cov-day-in-range"]
        : [];
    },
    [checkIn, checkOut],
  );

  const renderDayCell = useCallback(
    (arg: DayCellContentArg) => {
      const day = startOfDay(arg.date);
      const isPast = day < today;
      const count = isLoading || isPast ? null : countAvailable(day);
      const level =
        count === null || count === 0
          ? "none"
          : count === availability.length
            ? "all"
            : "some";

      return (
        <Styled.DayCell>
          <Styled.DayNumber>{arg.dayNumberText}</Styled.DayNumber>
          {count !== null && (
            <Styled.DayCount className="cov-day-count" $level={level}>
              {count === 0 ? "none" : `${count} available`}
            </Styled.DayCount>
          )}
        </Styled.DayCell>
      );
    },
    [availability.length, countAvailable, isLoading, today],
  );

  const available = stay
    ? availability.filter((entry) => isAvailable(entry, stay))
    : [];
  const unavailable = stay
    ? availability.filter((entry) => !isAvailable(entry, stay))
    : [];
  const feedsFailed = availability.some((entry) => entry.hadError);

  return (
    <Styled.Section>
      <Styled.Hint>
        Pick a date for a single night, then pick a second date for a longer
        stay — or drag across the days you want. Each day shows how many of our
        homes are available that night.
      </Styled.Hint>

      <Styled.Layout>
        <Styled.CalendarPane>
          <Styled.CalendarFrame>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{ left: "prev", center: "title", right: "next" }}
              height="auto"
              selectable
              selectMirror={false}
              unselectAuto={false}
              validRange={{ start: today }}
              dateClick={handleDateClick}
              select={handleSelect}
              dayCellClassNames={dayCellClassNames}
              dayCellContent={renderDayCell}
            />
          </Styled.CalendarFrame>

          <Styled.Legend>
            <Styled.LegendItem>
              <Styled.Swatch $color="#2f6470" /> your dates
            </Styled.LegendItem>
            <Styled.LegendItem>
              <Styled.Swatch $color="rgba(94, 139, 148, 0.3)" /> all homes available
            </Styled.LegendItem>
            <Styled.LegendItem>
              <Styled.Swatch $color="rgba(214, 168, 74, 0.38)" /> some homes available
            </Styled.LegendItem>
            <Styled.LegendItem>
              <Styled.Swatch $color="rgba(190, 90, 90, 0.26)" /> fully booked
            </Styled.LegendItem>
          </Styled.Legend>

          {feedsFailed && (
            <Styled.Notice>
              Some listing calendars could not be reached, so a few dates may be
              out of date. Please confirm with us before booking.
            </Styled.Notice>
          )}
        </Styled.CalendarPane>

        <Styled.ResultsPane>
          {isLoading && <SubTitle>Loading availability…</SubTitle>}

          {!isLoading && !stay && (
            <SubTitle>Select a date to see what is open.</SubTitle>
          )}

          {!isLoading && stay && (
            <>
              <Styled.SelectionBar>
                <Styled.SelectionText>
                  <strong>{formatStay(stay)}</strong>
                </Styled.SelectionText>
                <Styled.ClearButton type="button" onClick={clearSelection}>
                  Clear
                </Styled.ClearButton>
              </Styled.SelectionBar>

              <SubTitle>
                {available.length} of {availability.length}{" "}
                {available.length === 1 ? "home is" : "homes are"} available
              </SubTitle>

              <Styled.ResultList>
                {available.map((entry) => (
                  <HouseCard key={entry.house.propertyId} house={entry.house} />
                ))}
              </Styled.ResultList>

              {unavailable.length > 0 && (
                <>
                  <SubTitle>Booked for these dates</SubTitle>
                  <Styled.ResultList>
                    {unavailable.map((entry) => (
                      <HouseCard
                        key={entry.house.propertyId}
                        house={entry.house}
                        unavailable
                        conflict={firstConflict(entry, stay)}
                      />
                    ))}
                  </Styled.ResultList>
                </>
              )}

              <Styled.Notice>
                Availability comes from our listing calendars and can change
                quickly. Send us the dates below and we will confirm.
              </Styled.Notice>
            </>
          )}
        </Styled.ResultsPane>
      </Styled.Layout>
    </Styled.Section>
  );
}

export default AvailabilitySearch;
